/**
 * Format a GBP amount with "smart" decimals.
 *
 * Whole pounds render with no decimals (£500); amounts with pence render with
 * exactly two (£499.99, £1,245.50). Used for approval spend caps and
 * week-to-date spend, where rounding to whole pounds would misrepresent the
 * true configured cap or committed spend.
 *
 * @param value - The amount in pounds.
 * @returns The formatted string, e.g. "£500" or "£1,245.50".
 */
export function formatMoney(value: number): string {
  const hasFraction = value % 1 !== 0
  return `£${value.toLocaleString('en-GB', {
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: 2,
  })}`
}

/**
 * Format a GBP amount with a fixed number of decimals, e.g. "£1,245.50".
 *
 * @param value - The amount in pounds.
 * @param decimals - Decimals to show (0 rounds to whole pounds).
 * @returns The formatted string.
 */
export function formatCurrency(value: number, decimals = 0): string {
  return `£${value.toLocaleString('en-GB', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`
}

/**
 * Format a change in pounds with its sign, e.g. "+£2.34" or "−£12.75".
 *
 * @param value - The change in pounds (negative for a reduction).
 * @returns The formatted string; zero has no sign ("£0.00").
 */
export function formatPoundsDifference(value: number): string {
  const sign = value > 0 ? '+' : value < 0 ? '−' : ''
  return `${sign}${formatCurrency(Math.abs(value), 2)}`
}

// Order line unit prices are stored to 4 decimals (pack per-unit prices carry
// sub-penny precision, e.g. £3.26 / 100 = £0.0326).
const UNIT_PRICE_MIN_DECIMALS = 2
const UNIT_PRICE_MAX_DECIMALS = 4
const UNIT_PRICE_SCALE = 10 ** UNIT_PRICE_MAX_DECIMALS

/**
 * Format an order line's per-unit price with only the decimals it needs.
 *
 * Rounded half up to 4 decimals, as the backend stores it, then shown with at
 * least two: £5.49 stays "£5.49", a pack per-unit shows "£0.0326". The 1e-9
 * nudge matches `roundToPenny` so float error can't round a genuine half down.
 *
 * @param value - The per-unit price in pounds.
 * @returns The formatted string, e.g. "£5.49" or "£0.0326".
 */
export function formatUnitPrice(value: number): string {
  const rounded = Math.round((value + 1e-9) * UNIT_PRICE_SCALE) / UNIT_PRICE_SCALE
  return `£${rounded.toLocaleString('en-GB', {
    minimumFractionDigits: UNIT_PRICE_MIN_DECIMALS,
    maximumFractionDigits: UNIT_PRICE_MAX_DECIMALS,
  })}`
}
